import { useState, useMemo, useEffect, useCallback } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  useGetAllPostsQuery,
  useGetPostByIdQuery,
  useModeratePostMutation,
  useDeletePostMutation,
  PostAction,
} from '@/services/PostService';
import { IPostData, PostStatus, Post } from '@/types/post-management.types';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { PostModal } from './PostModal';
import { useSnackbar } from 'notistack';
import { ConfirmationDialog } from '@/components/dialogBox';
import { useGetCommunityByIdQuery } from '@/services/CommunityManagementService';
import { CommunityRole } from '@/types/community-user.types';
import { formatDisplayDate } from '@/utils/Date';

const PostListings = () => {
  const [moderatePost] = useModeratePostMutation();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    post: Post | null;
  }>({ isOpen: false, post: null });
  const [postIdToFetch, setPostIdToFetch] = useState<string | null>(null);
  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    postId: string | null;
    action: PostAction | 'DELETE_POST' | null;
  }>({ isOpen: false, postId: null, action: null });
  const [modalMode, setModalMode] = useState<'view' | 'edit'>('view');
  const [deletePost] = useDeletePostMutation();
  const { enqueueSnackbar } = useSnackbar();
  const communityId = useSelector(
    (state: RootState) => state.auth.selectedCommunityId
  );

  const getStatusEnum = (filter: string): string | undefined => {
    const statusMap: { [key: string]: string } = {
      active: PostStatus.ACTIVE,
      inactive: PostStatus.INACTIVE,
    };
    return filter !== 'all' ? statusMap[filter] : undefined;
  };

  const queryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    status: getStatusEnum(statusFilter),
    community_id: communityId || undefined,
  };

  const { data, isLoading, isFetching, isError, refetch } = useGetAllPostsQuery(
    queryParams,
    {
      skip: !communityId,
    }
  );

  const {
    data: postData,
    isSuccess: postFetchSuccess,
    error: postFetchError,
  } = useGetPostByIdQuery(
    { communityId: communityId || '', postId: postIdToFetch! },
    {
      skip: !communityId || !postIdToFetch,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: communityData } = useGetCommunityByIdQuery(communityId || '', {
    skip: !communityId,
  });

  const isCommunityAdmin =
    communityData?.user_role === CommunityRole.COMMUNITY_ADMIN;

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.total || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (postFetchSuccess && postData && postIdToFetch === postData.id) {
      setModalState((prev) => {
        if (prev.post?.id === postData.id && prev.post === postData)
          return prev;
        return { isOpen: true, post: postData };
      });
    }
  }, [postFetchSuccess, postData, postIdToFetch]);

  useEffect(() => {
    if (postFetchError) {
      enqueueSnackbar('Failed to fetch post details', { variant: 'error' });
      setPostIdToFetch(null);
    }
  }, [postFetchError, enqueueSnackbar]);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [statusFilter, debouncedValue, refetch]);

  const handleOpenPostModal = useCallback(
    (postId: string, mode: 'view' | 'edit' = 'view') => {
      setModalMode(mode);
      setModalState({ isOpen: true, post: null });
      setPostIdToFetch(postId);
    },
    []
  );

  const handleConfirmAction = async () => {
    if (confirmActionState.postId && confirmActionState.action && communityId) {
      try {
        if (confirmActionState.action === 'DELETE_POST') {
          await deletePost({
            communityId,
            postId: confirmActionState.postId,
          }).unwrap();
          enqueueSnackbar('Post deleted successfully', { variant: 'success' });
        } else {
          await moderatePost({
            postId: confirmActionState.postId,
            action: confirmActionState.action as PostAction,
          }).unwrap();

          enqueueSnackbar(
            `Post ${confirmActionState.action === PostAction.HIDE ? 'hidden' : 'approved'} successfully`,
            { variant: 'success' }
          );
        }

        if (typeof refetch === 'function') {
          refetch();
        }

        setConfirmActionState({
          isOpen: false,
          postId: null,
          action: null,
        });
      } catch (error) {
        console.error('Failed to update post status:', error);
        enqueueSnackbar('Failed to update post status', {
          variant: 'error',
        });
      }
    }
  };

  const filteredData = useMemo(() => {
    if (!data?.posts) return [];

    return data.posts.map((post) => ({
      id: post.id,
      title: post.title,
      content: post.content,
      author: {
        id: post.author.id,
        name: post.author.name || post.author.email,
      },
      status: post.status,
      created_at: post.created_at,
      likeCount: post.likeCount,
      commentCount: post.comments?.length || 0,
    }));
  }, [data]);

  const columns = useMemo(
    () => [
      {
        accessor: (row: IPostData) => row.title,
        id: 'title',
        header: 'Title',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPostData } }) => (
          <button
            type="button"
            onClick={() => handleOpenPostModal(row.original.id)}
            className="text-sm font-medium text-blue-600 hover:underline focus:outline-none contrast:text-white"
            aria-label={`View details for ${row.original.title}`}
          >
            {row.original.title}
          </button>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IPostData) => row.author.name,
        id: 'author',
        header: 'Author',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPostData } }) => {
          const displayText =
            row.original.author.name || row.original.author.email || 'Unknown';
          const isTruncated = displayText.length > 20;
          const truncatedText = isTruncated
            ? `${displayText.slice(0, 20)}...`
            : displayText;
          return (
            <div className="relative inline-block group">
              <span
                className="text-gray-800 font-normal truncate max-w-[150px] inline-block"
                aria-label={isTruncated ? displayText : undefined}
              >
                {truncatedText}
              </span>
              {isTruncated && (
                <div className="absolute z-50 bottom-full left-0 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded whitespace-normal max-w-xs shadow-lg transition-opacity duration-200 invisible group-hover:visible">
                  {displayText}
                </div>
              )}
            </div>
          );
        },
        meta: { className: 'min-w-[150px] max-w-[150px]' },
      },
      {
        accessor: (row: IPostData) => row.created_at,
        id: 'created_at',
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPostData } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.created_at))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IPostData) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPostData } }) => (
          <span
            data-testid={`status-${row.original.id}`}
            className={`text-sm font-medium ${
              row.original.status === PostStatus.ACTIVE
                ? 'text-green-600'
                : 'text-red-600'
            }`}
            role="status"
            aria-label={`Post status: ${row.original.status}`}
          >
            {row.original.status}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IPostData) => row.likeCount,
        id: 'likeCount',
        header: 'Likes',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPostData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.likeCount}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: IPostData) => row.commentCount,
        id: 'commentCount',
        header: 'Comments',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPostData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.commentCount}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IPostData } }) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleOpenPostModal(row.original.id, 'view')}
              className="btn btn-sm btn-icon btn-light btn-outline"
              aria-label={`View details for ${row.original.title}`}
              title="View post"
            >
              <KeenIcon type="filled" icon="eye" className="text-gray-500" />
            </button>
            {isCommunityAdmin && (
              <>
                <button
                  type="button"
                  onClick={() => handleOpenPostModal(row.original.id, 'edit')}
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Edit ${row.original.title}`}
                  title="Edit post"
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-gray-500"
                  />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setConfirmActionState({
                      isOpen: true,
                      postId: row.original.id,
                      action: 'DELETE_POST',
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Delete ${row.original.title}`}
                  title="Delete post"
                >
                  <KeenIcon
                    type="filled"
                    icon="trash"
                    className="text-gray-500"
                  />
                </button>
              </>
            )}
          </div>
        ),
        meta: { className: 'min-w-[120px]' },
      },
    ],
    [isCommunityAdmin, handleOpenPostModal]
  );

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex-shrink-0" role="search">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-posts"
              aria-label="Search posts"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="search-posts"
                placeholder="Search posts"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search posts by title or content"
                role="searchbox"
                disabled={isLoading || isFetching}
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter posts by status"
              disabled={isLoading || isFetching}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          role="toolbar"
          aria-label="Filter controls"
        >
          {(searchTerm || statusFilter !== 'all') && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={() => {
                resetSearch();
                setStatusFilter('all');
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              aria-label="Clear all filters"
              title="Clear all filters"
              disabled={isLoading || isFetching}
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching ? (
          <div
            className="text-center py-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
              aria-hidden="true"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isError ? (
          <div
            className="text-center py-4 text-red-600"
            role="alert"
            aria-live="assertive"
          >
            Error loading data. Please try again.
          </div>
        ) : (
          <>
            {!isLoading && !isError && filteredData.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-500">No posts available</p>
              </div>
            )}
            {!isLoading && !isError && filteredData.length > 0 && (
              <DataGrid
                data-testid="posts-data-grid"
                columns={columns}
                data={filteredData}
                rowSelect={false}
                loading={isLoading || isFetching}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  totalItems: data?.total || totalItems,
                  totalPages: data?.totalPages || totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                sorting={[{ id: 'title', desc: false }]}
                serverSide={true}
                hasActiveFilters={!!searchTerm || statusFilter !== 'all'}
                aria-label="Posts table"
              />
            )}
          </>
        )}
        <PostModal
          open={modalState.isOpen}
          onClose={() => {
            setModalState({ isOpen: false, post: null });
            setPostIdToFetch(null);
          }}
          post={modalState.post}
          mode={modalMode}
          communityId={communityId || ''}
        />
        <ConfirmationDialog
          data-testid="moderate-confirmation-dialog"
          open={confirmActionState.isOpen}
          onClose={() =>
            setConfirmActionState({ isOpen: false, postId: null, action: null })
          }
          onConfirm={handleConfirmAction}
          message={`Are you sure you want to ${confirmActionState.action === 'DELETE_POST' ? 'delete' : confirmActionState.action?.toLowerCase()} this post?`}
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="moderate-confirmation-title"
          aria-describedby="moderate-confirmation-description"
        />
      </div>
    </div>
  );
};

export default PostListings;
